<template>
  <div>
    <base-button block type="primary" class="m-0" @click="modal = true">
      {{ $t('seasonTicketRevisions.create.btnLabel') }}
    </base-button>

    <modal id="CreateDistributor"
           :show.sync="modal"
           modal-classes="modal-dialog-centered modal-lg"
           showClose @close="resetForm()">
      <template slot="header">
        <h4 class="modal-title">
          {{ $t('seasonTicketRevisions.create.titleText') }}
        </h4>
      </template>
      <template>
        <div class="text-center text-muted mb-4">
          <small>{{ $t('seasonTicketRevisions.create.helpText') }}</small>
        </div>
        <base-alert v-if="alert" dismissible :dismiss-callback="() => alert=false" type="danger">
          <!-- TODO: add error translation -->
          {{ getActionErrorDetails }}
        </base-alert>
        <form role="form" @submit.prevent="validate()">
          <div class="row">
            <label class="col-sm-3 col-form-label">{{ $t('generic.status') }}</label>
            <div class="col-sm-9">
              <base-input
                type="string"
                name="status"
                :value="formDetails.status"
                disabled>
              </base-input>
            </div>
          </div>
          <div class="row">
            <label class="col-sm-3 col-form-label">{{ $t('seasonTicketRevisions.details.place_of_purchase') }}</label>
            <div class="col-sm-9">
              <base-input
                :error="getError('place_of_purchase')">
                <el-select
                  name="place_of_purchase"
                  data-vv-as="'place_of_purchase'"
                  class="select-primary"
                  v-validate="modelValidations.required"
                  v-model="formDetails.place_of_purchase">
                  <el-option
                    v-for="option in placeOfPurchaseOptions"
                    class="select-primary"
                    :value="option"
                    :label="option"
                    :key="option">
                  </el-option>
                </el-select>
              </base-input>
            </div>
          </div>
          <div class="row">
            <label class="col-sm-3 col-form-label">{{ $t('seasonTicketRevisions.details.price') }}</label>
            <div class="col-sm-9">
              <base-input
                type="number"
                name="price"
                v-validate="modelValidations.required"
                :error="getError('price')"
                v-model="formDetails.price"
                step="0.01">
              </base-input>
            </div>
          </div>
          <div class="row">
            <label class="col-sm-3 col-form-label">{{ $t('seasonTicketRevisions.details.sale_starts_at') }}</label>
            <div class="col-sm-9">
              <base-input
                :error="getError('sale_starts_at')">
                <el-date-picker
                  name="sale_starts_at"
                  v-validate="modelValidations.required"
                  type="date"
                  v-model="formDetails.sale_starts_at"
                  placeholder="Pick sale from date"
                  :picker-options="pickerOptions">
                </el-date-picker>
              </base-input>
            </div>
          </div>
          <div class="row">
            <label class="col-sm-3 col-form-label">{{ $t('seasonTicketRevisions.details.sale_ends_at') }}</label>
            <div class="col-sm-9">
              <base-input
                :error="getError('sale_ends_at')">
                <el-date-picker
                  name="sale_starts_at"
                  type="date"
                  v-model="formDetails.sale_ends_at"
                  placeholder="Pick sale end date"
                  :picker-options="pickerOptions">
                </el-date-picker>
              </base-input>
            </div>
          </div>
          <div class="row">
            <label class="col-sm-3 col-form-label">{{
                $t('seasonTicketRevisions.details.max_start_postpone_days')
              }}</label>
            <div class="col-sm-9">
              <base-input
                type="number"
                name="max_start_postpone_days"
                v-validate="modelValidations.number"
                :error="getError('max_start_postpone_days')"
                v-model="formDetails.max_start_postpone_days">
              </base-input>
            </div>
          </div>
          <div class="row">
            <label class="col-sm-3  col-form-label">{{
                $t('seasonTicketRevisions.details.requires_additional_details')
              }}</label>
            <div class="col-sm-9">
              <div class="form-group">
                <tags-select-input v-model="formDetails.additionalData"
                                   input-placeholder="-"
                                   :options="additionalDataOptions"
                                   input-size="" tag-size="medium"></tags-select-input>
              </div>
            </div>
          </div>
          <div class="text-center">
            <base-button
              type="primary"
              class="my-4"
              @click="validate()"
              :loading="updating"
            >{{ $t('seasonTicketRevisions.create.actionBtnLabel') }}
            </base-button>
          </div>
        </form>
      </template>
    </modal>
  </div>
</template>


<script>
import {BaseAlert, Modal, TagsInput} from '@/components';
import {DatePicker, Option, Select} from 'element-ui';
import {mapActions, mapState} from "vuex";
import TagsSelectInput from "@/components/Inputs/TagsSelectInput";

export default {
  name: 'createSeasonTicketRevision',
  components: {
    TagsSelectInput,
    [Option.name]: Option,
    [Select.name]: Select,
    [DatePicker.name]: DatePicker,
    Modal,
    BaseAlert,
    TagsInput
  },
  props: {
    seasonTicketId: {
      type: String,
      required: true,
    }
  },
  data() {
    return {
      modal: false,
      alert: false,
      formDetails: {
        status: "not_approved",
        price: 1,
        sale_starts_at: new Date((new Date()).getFullYear(), (new Date()).getMonth(), (new Date()).getDate()),
        sale_ends_at: null,
        max_start_postpone_days: 1,
        place_of_purchase: '',
        additionalData: [],
        requires_additional_details: {
          personal_data: false,
          contacts: false,
          assignee: false,
          document: false,
        },
      },
      modelValidations: {
        required: {
          required: true
        },
        number: {
          numeric: true
        },
      },
      pickerOptions: {
        disabledDate(time) {
          return time.getTime() < new Date((new Date()).getFullYear(), (new Date()).getMonth(), (new Date()).getDate());
        },
      },
    }
  },
  computed: {
    ...mapState({
      loading: state => state.season_ticket_revisions.loading,
      updating: state => state.season_ticket_revisions.updating,
      error: state => state.season_ticket_revisions.error,
      created: state => state.season_ticket_revisions.created,
      placeOfPurchaseOptions: state => state.season_ticket_revisions.placeOfPurchaseOptions,
      additionalDataOptions: state => state.season_ticket_revisions.additionalDataOptions,
    }),
    getActionErrorDetails() {
      return this.error ? this.error.bodyText : null;
    },
  }, methods: {
    ...mapActions(['CreateSeasonTicketRevision', 'GetSeasonTicket']),
    getError(fieldName) {
      return this.errors.first(fieldName);
    },
    validate() {
      this.$validator.validateAll().then(isValid => {
        if (isValid) {
          this.create()
        }
      });
    },
    create: function () {
      let requiresAdditionalDetails = {
        personal_data: this.formDetails.additionalData.includes("personal_data"),
        contacts: this.formDetails.additionalData.includes("contacts"),
        assignee: this.formDetails.additionalData.includes("assignee"),
        document: this.formDetails.additionalData.includes("document"),
      }

      if (!requiresAdditionalDetails.personal_data
        && !requiresAdditionalDetails.contacts
        && !requiresAdditionalDetails.assignee
        && !requiresAdditionalDetails.document) {
        requiresAdditionalDetails = null
      }

      let payload = {
        seasonTicketId: this.seasonTicketId,
        data: {
          status: this.formDetails.status,
          price: Math.trunc(this.formDetails.price * 100),
          sale_starts_at: this.formDetails.sale_starts_at,
          sale_ends_at: this.formDetails.sale_ends_at,
          max_start_postpone_days: this.formDetails.max_start_postpone_days * 1,
          place_of_purchase: this.formDetails.place_of_purchase,
          requires_additional_details: requiresAdditionalDetails,
        }
      }
      this.CreateSeasonTicketRevision(payload).then(() => {
        if (this.error != null) {
          this.alert = true
          // got error, don't reset form
        } else {
          this.$notify({
            type: 'success',
            message: this.$t('seasonTicketRevisions.create.createdNotification', this.created),
            icon: 'tim-icons icon-alert-circle-exc'
          })
          this.GetSeasonTicket(this.seasonTicketId)

          this.resetForm()
        }
      });
    },
    resetForm: function () {
      this.modal = false;
      this.alert = false;
      // resetting form
      this.formDetails = {
        status: "not_approved",
        price: 1,
        sale_starts_at: new Date((new Date()).getFullYear(), (new Date()).getMonth(), (new Date()).getDate()),
        sale_ends_at: null,
        max_start_postpone_days: 1,
        place_of_purchase: '',
        additionalData: [],
        requires_additional_details: {
          personal_data: false,
          contacts: false,
          assignee: false,
          document: false,
        },
      }
      // reset validation errors
      this.$validator.reset()

    },
  },
  mounted() {
  },
}
</script>
